// ── Sync layer ───────────────────────────────────────────────────────────────
// Mirrors all `clarity_*` localStorage keys to Firestore at users/{uid}, with:
//   • initial merge on login (local data is never silently lost),
//   • live cross-device pull via an onSnapshot listener,
//   • debounced push of local changes (cheap on Firestore reads/writes).
//
// Design choice: instead of editing the 1.2MB App.jsx everywhere it touches
// localStorage, we *wrap* localStorage.setItem/removeItem once. Every write the
// app makes to a clarity_* key is captured and queued for the cloud. localStorage
// stays the instant, offline-capable source the UI reads from.
import {
  doc, getDoc, setDoc, updateDoc, onSnapshot, deleteField,
} from 'firebase/firestore';
import { db } from './config';

const PREFIX = 'clarity_';
// How long an edit sits queued before it's pushed to Firestore. Short enough that
// cross-device sync feels responsive, but still long enough to coalesce a burst of
// rapid edits (e.g. typing) into one write instead of one per keystroke.
const PUSH_DEBOUNCE_MS = 500;

// Records which Firebase uid the local clarity_* data currently belongs to.
// Deliberately NOT prefixed with `clarity_` so it never gets swept into the
// synced cloud store. When a DIFFERENT uid signs in, we wipe local data first
// so one account's progress can never leak into (or seed) another's.
const OWNER_KEY = 'clarityOwnerUid';

// Keys whose values are append-only {id: timestamp} maps (tombstones / deletion
// records). These must be UNION-merged across devices, never overwritten: a device
// that hasn't yet seen a deletion would otherwise clobber the tombstone with its
// older map and resurrect the deleted item everywhere. Union-merge means the cloud
// always converges to the full set of deletions, so a delete can never come back.
const UNION_MERGE_KEYS = new Set(['clarity_deletedHabits']);

// Google Calendar/Tasks sync engines (src/google/sync.js, src/google/tasksSync.js)
// keep their own private "what did I last see from Google" diff snapshot in these
// clarity_*-prefixed keys, purely so THIS device's sync loop can tell what changed
// since its last run. They must never be cross-device mirrored: if a stale/older
// snapshot from another device (or an earlier session) got pulled in and clobbered
// the current one, the sync engine would misread its own tasks/events as "deleted
// locally" and push a REAL delete to the user's Google account to match — silent
// data loss on Google's side caused by nothing more than this device syncing.
// Excluded here from both the push and the remote-wins pull so each device's copy
// stays untouched by any other device or session.
const LOCAL_ONLY_KEYS = new Set([
  'clarity_gcalSync', 'clarity_gtaskSync', 'clarity_gcalCalendarId', 'clarity_gtaskListId',
  // Onboarding quiz progress is per-device. The quiz now runs AFTER sign-in, so
  // without this a stale draft in the cloud could pull a device back mid-quiz.
  'clarity_onboardingDraft',
]);
const isSyncable = (k) => typeof k === 'string' && k.startsWith(PREFIX) && !LOCAL_ONLY_KEYS.has(k);

// Keys whose owning feature has been taught to listen for `clarity:remote-update`
// (see startListening below) and patch its own React state directly instead of
// only finding out about a remote change on next reload. When a remote snapshot's
// changes are ENTIRELY covered by this set, the usual full-page safeReload() is
// skipped — the listening feature is trusted to have already reflected the change.
// Any key not in here keeps today's reload-based behavior untouched.
const LIVE_PATCH_KEYS = new Set([
  'clarity_diary',
  // Habits pilot: clarity_deletedHabits must travel WITH clarity_habits — a
  // remote delete arrives as a change to both keys (the tombstone union-merge,
  // then the filtered habits array), and liveOnly below only skips the reload
  // when every changed key in the snapshot is covered here.
  'clarity_habits', 'clarity_deletedHabits',
  'clarity_habitLogs', 'clarity_habitSkips', 'clarity_cleanLog',
  // Planner/Calendar pilot: deletion here is a plain array-filter (no tombstone
  // map like habits has), so each key is independent — no pairing required.
  'clarity_calEvents', 'clarity_tasks', 'clarity_calReminders',
  'clarity_taskOccOverrides', 'clarity_calEventOccOverrides',
  // Focus/Clock-in pilot: ONLY the completed-session history, a plain
  // append-only array — same shape as calEvents. Deliberately NOT included:
  // the actively-running countdown timer itself (clarity_focusStart/
  // Duration/Paused/Remaining/WorkMins, clarity_sw*). That's a ticking
  // setInterval driven by local refs, only ever read once on mount to resume
  // after a reload — live-patching it would mean hijacking one device's
  // in-progress countdown from another device's push mid-session, which is a
  // real product decision (does a phone timer "follow" you to a desktop
  // tab?), not just a sync-latency fix. Left as reload-based, unchanged.
  'clarity_focusSessions',
]);

function parseMap(s) {
  if (!s) return {};
  try { const o = JSON.parse(s); return o && typeof o === 'object' ? o : {}; }
  catch { return {}; }
}
// Union two {id: timestamp} maps, keeping the latest timestamp per id.
function unionMaps(a, b) {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) {
    if (!(k in out) || Number(v) > Number(out[k])) out[k] = v;
  }
  return out;
}

// Tombstone-key → the array-key it deletes items from (matched by element `.id`).
const TOMBSTONE_TARGETS = { clarity_deletedHabits: 'clarity_habits' };

// Drop every element of an array-JSON whose id is tombstoned. Returns the SAME
// string reference when nothing changed, so callers can cheaply detect a no-op.
// This is what actually heals a deletion: even if the cloud blob still contains a
// deleted habit (because the deleting device was killed before its push landed),
// every device strips it on read and pushes the cleaned blob back — so the habit
// can never reappear, and the cloud converges to the deleted state.
function filterTombstoned(arrJson, tombMap) {
  if (!arrJson || !tombMap || !Object.keys(tombMap).length) return arrJson;
  try {
    const arr = JSON.parse(arrJson);
    if (!Array.isArray(arr)) return arrJson;
    const next = arr.filter(el => el && !(String(el.id) in tombMap));
    return next.length === arr.length ? arrJson : JSON.stringify(next);
  } catch { return arrJson; }
}

// Persisted (but NOT synced) key that records when the user last wiped local
// data via a reset. Not prefixed with `clarity_` so it is never included in the
// synced Firestore store and never swept by the reset loop itself.
const RESET_MARKER_KEY = 'clarityLastLocalReset';

// Tracks the last time the user touched the page (tap/click/key/scroll), so a
// cross-device reload can wait for a natural pause instead of yanking the page
// out from under someone mid-scroll or mid-navigation. Attached once at module
// load — this file is a singleton, loaded for the lifetime of the tab.
// Starts at "now" (not 0): on a cold start, no interaction has happened yet, so
// `Date.now() - 0` looks like an eternity of idle time and a same-boot sync
// reload would fire almost instantly — right as the user opens the app, which
// is the exact opposite of what this idle check exists to prevent (that's what
// caused the double loading-screen flash on some cold starts).
let lastActivityAt = typeof window !== 'undefined' ? Date.now() : 0;
if (typeof window !== 'undefined') {
  const trackActivity = () => { lastActivityAt = Date.now(); };
  ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach(evt =>
    window.addEventListener(evt, trackActivity, { passive: true, capture: true })
  );
}
const IDLE_BEFORE_RELOAD_MS = 900;
const MAX_RELOAD_WAIT_MS = 8000;

// Reload safely, with storm protection so a sync that keeps reporting "changed"
// can never spin the page into an endless reload/blink loop. The data is always
// applied to localStorage BEFORE we reload, so when we suppress a reload here the
// UI just reflects the change on its next natural mount/navigation instead of
// instantly — a fine trade to avoid a flickering, unclickable app.
//   • never reload twice within 5s (breaks a tight loop: each boot is < 5s),
//   • rapid reloads accumulate toward a cap; well-spaced ones reset it, so a
//     genuine cross-device refresh every few minutes is never throttled.
//   • waits for the user to go idle (or the tab to be hidden) before actually
//     reloading, so a change synced from another tab/device never interrupts
//     something like navigating back out of a note mid-tap.
function doReloadNow() {
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem('clarityLastReloadAt') || 0);
    let count = Number(sessionStorage.getItem('clarityReloadCount') || 0);
    if (now - last < 5000) return false;        // too soon → almost certainly a loop
    if (now - last > 60000) count = 0;          // well-spaced → not a loop, reset the tally
    if (count >= 6) return false;               // many rapid reloads → stop the loop
    sessionStorage.setItem('clarityLastReloadAt', String(now));
    sessionStorage.setItem('clarityReloadCount', String(count + 1));
  } catch { /* sessionStorage unavailable → fall through and reload normally */ }
  window.location.reload();
  return true;
}

function safeReload() {
  const isIdle = () => document.visibilityState === 'hidden' || (Date.now() - lastActivityAt >= IDLE_BEFORE_RELOAD_MS);
  if (isIdle()) { doReloadNow(); return; }
  let waited = 0;
  const poll = setInterval(() => {
    waited += 500;
    if (isIdle() || waited >= MAX_RELOAD_WAIT_MS) { clearInterval(poll); doReloadNow(); }
  }, 500);
}

let currentUid = null;
let unsubscribe = null;
let pushTimer = null;
let pending = {};            // { key: value | null(=delete) } queued for cloud
let inFlight = {};           // keys pushed but not yet server-confirmed (clobber-protected)
let applyingRemote = false;  // guard so remote→local writes don't echo back up
let patched = false;

// `pending` above lives only in memory, so a reload (or the app process getting
// killed, e.g. Android backgrounding it) within the PUSH_DEBOUNCE_MS window drops
// it before it ever reaches Firestore. The NEXT startSync() then does its normal
// remote-wins merge and pulls the stale pre-edit cloud copy back over the fresh
// local edit — silently reverting it. Mirroring `pending` into localStorage lets
// startSync() detect this on the next boot and make local win for those keys
// instead of remote. Not `clarity_`-prefixed so it's never itself synced/swept.
const PENDING_KEY = 'clarityPendingPush';
function persistPending() {
  try {
    if (Object.keys(pending).length) localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    else localStorage.removeItem(PENDING_KEY);
  } catch {}
}
function loadPersistedPending() {
  try { return JSON.parse(localStorage.getItem(PENDING_KEY) || '{}') || {}; }
  catch { return {}; }
}

// Wipe every clarity_* key from localStorage WITHOUT echoing the removals to the
// cloud (we use the same applyingRemote guard the remote→local path uses) and
// drop any queued/in-flight pushes so a stale delete can't land on the next
// account. Used when a different user signs in on this device.
function wipeLocalData() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PREFIX)) keys.push(k);
  }
  applyingRemote = true;
  try {
    keys.forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem(OWNER_KEY);
    localStorage.removeItem(PENDING_KEY);
  } finally { applyingRemote = false; }
  clearTimeout(pushTimer);
  pending = {};
  inFlight = {};
}

// Snapshot every syncable clarity_* key currently in localStorage (excludes
// LOCAL_ONLY_KEYS — see above).
function collectLocal() {
  const out = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (isSyncable(k)) out[k] = localStorage.getItem(k);
  }
  return out;
}

// Write a {key: value} map into localStorage without re-triggering a cloud push.
function applyToLocal(map) {
  applyingRemote = true;
  try {
    Object.entries(map).forEach(([k, v]) => {
      if (v === null || v === undefined) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
    });
  } finally {
    applyingRemote = false;
  }
}

// Wrap localStorage so the app's existing writes flow to the cloud. Done once.
function patchLocalStorage() {
  if (patched) return;
  patched = true;
  const origSet = window.localStorage.setItem.bind(window.localStorage);
  const origRemove = window.localStorage.removeItem.bind(window.localStorage);

  window.localStorage.setItem = function (k, v) {
    const str = String(v);
    // Skip queueing a push for a write that doesn't actually change the value.
    // Matters for LIVE_PATCH_KEYS: those get applied to React state directly from
    // a remote update (see startListening), and React's own save-on-change effect
    // then re-writes the same value back to localStorage — without this check that
    // redundant write would echo an unnecessary push straight back to Firestore.
    const unchanged = !applyingRemote && isSyncable(k) && window.localStorage.getItem(k) === str;
    origSet(k, v);
    if (!applyingRemote && isSyncable(k) && !unchanged) {
      queuePush(k, str);
    }
  };
  window.localStorage.removeItem = function (k) {
    origRemove(k);
    if (!applyingRemote && isSyncable(k)) {
      queuePush(k, null);
    }
  };

  // The debounced queue lives in memory only, so a reload, tab close, or the app
  // being backgrounded within PUSH_DEBOUNCE_MS of an edit would drop it before it
  // reaches the cloud. Flush immediately when the app goes to the background so a
  // just-added item (e.g. a calendar block) is never lost.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && Object.keys(pending).length) {
        clearTimeout(pushTimer);
        flushPush();
      }
    });
  }
}

function queuePush(key, value) {
  pending[key] = value;
  persistPending();
  clearTimeout(pushTimer);
  pushTimer = setTimeout(flushPush, PUSH_DEBOUNCE_MS);
}

async function flushPush() {
  if (!currentUid) return;
  const batch = pending;
  pending = {};
  persistPending();
  if (Object.keys(batch).length === 0) return;

  // Protect these keys from a remote snapshot that predates this write. The server
  // can deliver an onSnapshot reflecting *other* changes before it has processed
  // ours; without this guard, that stale snapshot overwrites the value we just
  // saved (e.g. a freshly-added calendar block) and triggers a reload that loses it.
  Object.assign(inFlight, batch);

  // Use dotted field paths so each key merges independently (no full-doc clobber).
  const update = { updatedAt: Date.now() };
  for (const [k, v] of Object.entries(batch)) {
    update[`store.${k}`] = v === null ? deleteField() : v;
  }
  try {
    await updateDoc(doc(db, 'users', currentUid), update);
  } catch (e) {
    // Re-queue on failure so we retry on the next change (Firestore also keeps
    // an offline write queue, so this is mostly a belt-and-suspenders guard).
    pending = { ...batch, ...pending };
    persistPending();
    // eslint-disable-next-line no-console
    console.warn('[Clarity] sync push failed, will retry', e);
  } finally {
    // Release protection now the write is server-confirmed — but keep any key a
    // newer local edit has already re-queued into `pending`.
    for (const k of Object.keys(batch)) {
      if (!(k in pending)) delete inFlight[k];
    }
  }
}

function startListening(uid) {
  if (unsubscribe) unsubscribe();
  unsubscribe = onSnapshot(doc(db, 'users', uid), async (snap) => {
    // Ignore events caused by our own un-acked local writes (avoids reload loops).
    if (snap.metadata.hasPendingWrites || !snap.exists()) return;
    const remote = snap.data().store || {};
    let changed = false;
    const toApply = {};
    // Effective tombstone map = what's local ∪ whatever this snapshot carries, so
    // we can strip deleted habits out of the incoming habits blob below.
    let tombMap = parseMap(localStorage.getItem('clarity_deletedHabits'));
    if ('clarity_deletedHabits' in remote) {
      tombMap = unionMaps(tombMap, parseMap(remote['clarity_deletedHabits']));
    }
    const targetKeys = new Set(Object.values(TOMBSTONE_TARGETS));
    for (const [k, v] of Object.entries(remote)) {
      // Never pull a device-local sync-bookkeeping key down from the cloud, even
      // if old data for it is still sitting in Firestore from before this guard
      // existed — see LOCAL_ONLY_KEYS above.
      if (LOCAL_ONLY_KEYS.has(k)) continue;
      // Tombstone keys are union-merged, not overwritten — so a remote deletion
      // record is always absorbed (and re-pushed) even if this device also has a
      // pending edit to it. This is what makes a cross-device delete stick.
      if (UNION_MERGE_KEYS.has(k)) {
        const merged = JSON.stringify(unionMaps(parseMap(localStorage.getItem(k)), parseMap(v)));
        if (merged !== localStorage.getItem(k)) { toApply[k] = merged; changed = true; }
        if (merged !== v) queuePush(k, merged); // help the cloud converge to the full set
        continue;
      }
      // A fresh local edit may be sitting in the debounced push queue (`pending`)
      // or mid-flight to the server (`inFlight`), not yet readable in this snapshot.
      // The remote copy is therefore STALE for that key — never let it overwrite the
      // local value, or the in-progress edit (e.g. a just-saved note or calendar
      // block) is silently destroyed.
      if (k in pending || k in inFlight) continue;
      // Strip tombstoned items from target blobs (e.g. clarity_habits) and heal
      // the cloud, so another device's stale array can't resurrect a deletion.
      let val = v;
      if (targetKeys.has(k)) {
        val = filterTombstoned(v, tombMap);
        if (val !== v) queuePush(k, val);
      }
      if (localStorage.getItem(k) !== val) { toApply[k] = val; changed = true; }
    }
    if (!changed) return;
    // Persist any queued local edits BEFORE reloading. Otherwise the reload re-runs
    // startSync (remote-wins), which would clobber those un-pushed changes.
    if (Object.keys(pending).length) { clearTimeout(pushTimer); await flushPush(); }
    applyToLocal(toApply);
    // Let any feature that's opted in (LIVE_PATCH_KEYS) patch its own React state
    // directly instead of waiting for a reload. Harmless no-op for a tab with no
    // listener — this is just a DOM CustomEvent, nothing subscribes by default.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('clarity:remote-update', { detail: toApply }));
    }
    const changedKeys = Object.keys(toApply);
    const liveOnly = changedKeys.length > 0 && changedKeys.every(k => LIVE_PATCH_KEYS.has(k));
    if (liveOnly) return; // fully handled by a live listener — no reload needed
    // The app reads localStorage into React state on mount, so the simplest
    // reliable way to reflect another device's changes is a soft reload —
    // guarded so a snapshot that keeps reporting "changed" can't loop.
    safeReload();
  });
}

// Call immediately when the user triggers any reset so the cloud push starts
// without waiting for the 1500 ms debounce window.
export function flushNow() {
  clearTimeout(pushTimer);
  flushPush(); // fire-and-forget; the async write runs in the background
}

// Record the current timestamp as the "last local reset" instant. Called before
// any reset clears localStorage so that startSync can detect the case where the
// cloud is stale (push hadn't landed yet) and avoid overwriting the fresh reset.
export function markReset() {
  try { localStorage.setItem(RESET_MARKER_KEY, String(Date.now())); } catch {}
}

// Call right after login. Returns `true` if the cloud held data that differed
// from what was already on this device (so a background-rendered app knows it
// should refresh to reflect newer cross-device changes).
export async function startSync(uid) {
  currentUid = uid;

  // If this device's local data belongs to a DIFFERENT account, wipe it before
  // merging. Otherwise the previous account's clarity_* data (still sitting in
  // localStorage after a plain sign-out) would seed an empty cloud or leak its
  // local-only keys up — which is exactly the "switched accounts but saw the old
  // account's progress" bug. Same-uid reopen skips this and resumes instantly.
  let owner = null;
  try { owner = localStorage.getItem(OWNER_KEY); } catch {}
  const wiped = Boolean(owner) && owner !== uid;
  if (wiped) wipeLocalData();
  try { localStorage.setItem(OWNER_KEY, uid); } catch {}

  const ref = doc(db, 'users', uid);
  const local = collectLocal();
  let changed = false;

  // Keys still sitting in a persisted-but-never-flushed push queue from a
  // previous session (the tab/app closed or reloaded inside the 1500ms debounce
  // window before the write reached Firestore — see PENDING_KEY above). `local`
  // already reflects the edit (it was written to localStorage synchronously),
  // so these keys must win over whatever Firestore currently has, not be
  // clobbered by the remote-wins merge below.
  const leftoverPending = loadPersistedPending();
  const leftoverKeys = Object.keys(leftoverPending).filter(isSyncable);

  // Everything from here through the reconcile below used to run unguarded: if
  // even the initial getDoc() read failed (network blip, anything), the thrown
  // error propagated out of startSync() entirely, so patchLocalStorage() and
  // startListening() at the bottom never ran — syncing silently never activated
  // for the rest of the session, with only a swallowed console.warn in AuthGate
  // as any sign of it. Wrapping the whole reconcile means a failure anywhere in
  // here degrades to "retry local's current state via the normal debounced
  // queue" instead of "no sync at all this session."
  try {
  const snap = await getDoc(ref);

  if (snap.exists()) {
    const remote = snap.data().store || {};
    // Tombstone keys union-merge (local ∪ remote) so deletions made on THIS device
    // but not yet acked by the cloud survive the "remote wins" overwrite below — a
    // deleted habit can never reappear just because the cloud blob is momentarily
    // stale. The merged map is pushed back up so every device converges.
    // Device-local sync-bookkeeping keys (LOCAL_ONLY_KEYS) are stripped out even if
    // stale data for them exists in the cloud from before this guard existed — this
    // device's copy is always the one that matters.
    const remoteToApply = { ...remote };
    for (const k of LOCAL_ONLY_KEYS) delete remoteToApply[k];
    const tombstonePush = {};
    for (const key of UNION_MERGE_KEYS) {
      if (!(key in remote) && !localStorage.getItem(key)) continue;
      const merged = JSON.stringify(unionMaps(parseMap(localStorage.getItem(key)), parseMap(remote[key])));
      remoteToApply[key] = merged;
      if (merged !== (remote[key] || undefined)) tombstonePush[key] = merged;
    }
    // Strip tombstoned items out of their target blobs (remote-wins value) so a
    // stale cloud copy can't resurrect a deleted habit, and push the cleaned blob
    // up so the cloud heals. Runs AFTER the union loop so tombMap is fully merged.
    for (const [tombKey, arrKey] of Object.entries(TOMBSTONE_TARGETS)) {
      if (!(arrKey in remoteToApply)) continue;
      const tombMap = parseMap(remoteToApply[tombKey] || localStorage.getItem(tombKey));
      const cleaned = filterTombstoned(remoteToApply[arrKey], tombMap);
      if (cleaned !== remoteToApply[arrKey]) {
        remoteToApply[arrKey] = cleaned;
        tombstonePush[arrKey] = cleaned;
      }
    }
    // If this device performed a reset more recently than Firestore's last write,
    // the remote copy is stale: the reset push hadn't landed before the app was
    // closed. Skip overwriting local data with the stale cloud copy and instead
    // push the freshly-reset local state up so the cloud heals to the reset state.
    const localResetAt = Number(localStorage.getItem(RESET_MARKER_KEY) || 0);
    const remoteUpdatedAt = Number(snap.data().updatedAt || 0);
    const localResetWins = localResetAt > 0 && localResetAt > remoteUpdatedAt;

    if (localResetWins) {
      // Local reset is newer than Firestore — push the reset state up, don't pull.
      // We do NOT touch local here, so this is NOT a "changed" that warrants a
      // reload: local already reflects the reset the user is looking at. Returning
      // changed=true here was a bug — if the push lagged, every boot re-entered this
      // branch and reloaded, blinking the app endlessly.
      const resetPayload = { updatedAt: Date.now() };
      for (const [k, v] of Object.entries(local)) resetPayload[`store.${k}`] = v;
      // Null-out every key the cloud has that local no longer has (the reset wiped them).
      for (const k of Object.keys(remote)) {
        if (!(k in local)) resetPayload[`store.${k}`] = deleteField();
      }
      // This push (and the two below) used to run unguarded: a single failure — a
      // network blip, anything — threw out of startSync() entirely, so the
      // patchLocalStorage()/startListening() calls at the bottom never ran and
      // syncing silently never activated for the rest of the session (the only
      // sign was a swallowed console.warn in AuthGate). Catching here means a
      // failed push degrades to "retry via the normal debounced queue" instead
      // of "no sync at all this session."
      try { await updateDoc(ref, resetPayload); }
      catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[Clarity] initial reset push failed, will retry', e);
        for (const k of Object.keys(remote)) if (!(k in local)) pending[k] = null;
        for (const [k, v] of Object.entries(local)) pending[k] = v;
        persistPending();
      }
    } else {
      // A leftover pending key never made it to Firestore last session, so the
      // copy we're about to pull down for it is stale — keep local's newer
      // value instead of letting remote overwrite it.
      for (const k of leftoverKeys) delete remoteToApply[k];
      // Detect whether the cloud differs from our current local copy BEFORE we
      // overwrite it. Same-device reopen → no diff → caller can resume seamlessly.
      // Only the remote-wins path can be "changed": it's the only one that mutates
      // local, so it's the only one a reload needs to reflect.
      for (const [k, v] of Object.entries(remoteToApply)) {
        if (localStorage.getItem(k) !== v) { changed = true; break; }
      }
      // Remote wins for shared keys; local-only keys are preserved and pushed up.
      applyToLocal(remoteToApply);
      const localOnly = {};
      for (const [k, v] of Object.entries(local)) {
        if (!(k in remote)) localOnly[k] = v;
      }
      // Push the leftover pending keys up now, since they won't be re-queued
      // in memory the way a normal edit would be — that queue was lost on reload.
      for (const k of leftoverKeys) {
        if (k in local) localOnly[k] = local[k];
      }
      const update = { ...localOnly, ...tombstonePush };
      if (Object.keys(update).length) {
        const payload = { updatedAt: Date.now() };
        for (const [k, v] of Object.entries(update)) payload[`store.${k}`] = v;
        try { await updateDoc(ref, payload); }
        catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[Clarity] initial sync push failed, will retry', e);
          Object.assign(pending, update);
          persistPending();
        }
      }
    }
  } else {
    // First login on this account — seed the cloud with whatever is on this
    // device. IMPORTANT: this used to be setDoc(ref, { store: local, ... }),
    // a full-document replace. getDoc() falsely reporting "doesn't exist" for
    // a document that actually does (a network blip, a bad read — rare, but
    // real: this is what caused real user data loss) meant that full replace
    // silently destroyed every field the false-negative read didn't know
    // about. Writing each key as its own dotted `store.<key>` path with
    // merge:true is safe either way: on a genuinely new doc it creates it
    // with exactly these fields (identical result to before); on a false
    // negative it only touches the specific keys this device has, leaving
    // every other field in the real document completely untouched.
    const seedPayload = { updatedAt: Date.now() };
    for (const [k, v] of Object.entries(local)) seedPayload[`store.${k}`] = v;
    try { await setDoc(ref, seedPayload, { merge: true }); }
    catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Clarity] initial seed push failed, will retry', e);
      Object.assign(pending, local);
      persistPending();
    }
  }

  // Every branch above either successfully pushed the leftover keys (nothing
  // left to persist) or, on failure, re-queued them into `pending` and already
  // called persistPending() itself. Re-syncing the marker here — instead of
  // blindly clearing it — makes sure a failed retry's marker survives rather
  // than being wiped out by an unconditional removeItem right after we just
  // wrote it.
  if (leftoverKeys.length) persistPending();
  } catch (e) {
    // The initial read (or something before any of the inner catches could run)
    // failed. We don't know what, if anything, made it to Firestore, so the safe
    // fallback is to queue local's entire current state for retry — worst case
    // a few keys get pushed again harmlessly once the connection recovers.
    // eslint-disable-next-line no-console
    console.warn('[Clarity] initial sync reconcile failed, will retry from local state', e);
    Object.assign(pending, local);
    persistPending();
  }

  patchLocalStorage();
  startListening(uid);
  // A wipe always counts as "changed": React state was built from the previous
  // account's data, so the gate must reload to rebuild it from this account's.
  return changed || wiped;
}

// Loop-protected page reload, shared with AuthGate's initial-sync refresh.
export { safeReload };

// Call on sign-out.
export function stopSync() {
  if (unsubscribe) { unsubscribe(); unsubscribe = null; }
  clearTimeout(pushTimer);
  pending = {};
  inFlight = {};
  currentUid = null;
}

// Wipe every clarity_* key from this device's localStorage. Exposed for
// account deletion — call after stopSync() so nothing gets pushed back up.
export { wipeLocalData };
